<template>
  <DatatableReportV3
    v-if="tenantLoaded"
    ref="pending_revenue_datatable"
    title="Pending Revenue"
    :source="source"
    :config="config"
    :filters="filters"
  />
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { customerClassificationColumns } from "@/lib/customerTypes";

import CONSTANTS from "@/Constants.js";
import DepartmentLink from "@/Department/DepartmentLink.vue";
import JobOpportunityLink from "@/Job/Opportunity/JobOpportunityLink.vue";
import { trackEvent } from "@/lib/pendo";
import AccountingClassLink from "@/Reports/AccountingClassLink.vue";
import DatatableReportV3 from "@/Reports/DatatableReportV3.vue";
import TagList from "@/Tags/TagList.vue";
import store from "@/vuex/store";
import RevenueActionsContextMenu from "./RevenueActionsContextMenu.vue";

export default {
  components: {
    DatatableReportV3
  },
  props: {
    source: {
      type: String,
      default: ""
    },
    filters: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    ...mapGetters(["showAccountingClasses"]),
    ...mapState({
      tenantLoaded({ tenant }) {
        return !!tenant;
      }
    }),
    config() {
      return {
        responsive: true,
        order: [[0, "desc"]],
        columns: [
          {
            data: "invoice",
            aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.invoices,
            responsivePriority: 2
          },
          ...customerClassificationColumns(),
          {
            data: "customer",
            aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.customers,
            responsivePriority: 1
          },
          { data: "customer_email", title: "Email", responsivePriority: 11 },
          { data: "customer_phone", title: "Phone", responsivePriority: 5 },
          {
            data: "billing_address",
            title: "Billing Address",
            orderable: false,
            responsivePriority: 12
          },
          { data: "job", responsivePriority: 6 },
          {
            data: "tags",
            title: "Tags",
            orderable: false,
            searchable: false,
            responsivePriority: 15
          },
          {
            data: "location_name",
            title: "Location Name",
            orderable: false,
            responsivePriority: 14
          },
          {
            data: "service_address",
            title: "Service Address",
            orderable: false,
            responsivePriority: 13
          },
          {
            data: "last_appointment_time",
            title: "Last Appointment",
            searchable: false,
            responsivePriority: 9
          },
          {
            data: "invoice_last_emailed_at",
            title: "Last Emailed",
            searchable: false,
            responsivePriority: 10
          },
          {
            data: "department",
            aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.departments,
            filterName: "department_id",
            searchable: false,
            responsivePriority: 8
          },
          {
            data: "technician",
            title: "Owner",
            filterName: "technician_id",
            responsivePriority: 7
          },
          {
            data: "accounting_class",
            title: "Class",
            searchable: false,
            orderable: false,
            visible: this.showAccountingClasses,
            responsivePriority: 16
          },
          {
            data: "revenue",
            className: "text-right",
            footerClassName: "aggregate-currency",
            aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.revenue,
            searchable: false,
            responsivePriority: 4
          },
          {
            data: "balance",
            title: "Balance",
            className: "text-right",
            searchable: false,
            aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.balance,
            footerClassName: "aggregate-currency",
            footerCellClass: "text-danger",
            filterName: "paid",
            responsivePriority: 3
          },
          {
            data: "actions",
            title: "",
            searchable: false,
            orderable: false,
            className: "text-center th-no-text"
          }
        ],
        vueComponents: [
          {
            rootId: "department-modal-vue",
            component: DepartmentLink,
            options: {
              store
            }
          },
          {
            rootId: "job-opportunity-modal-vue",
            component: JobOpportunityLink,
            options: {
              store
            }
          },
          {
            rootId: "accounting-class-link-vue",
            component: AccountingClassLink,
            options: {
              store
            }
          },
          {
            rootId: "invoices-context-menu-vue",
            component: RevenueActionsContextMenu,
            options: {
              store
            }
          },
          {
            rootId: "tag-list-vue",
            component: TagList
          }
        ]
      };
    }
  },
  mounted() {
    this.$store.dispatch("addBreadcrumb", { label: "Pending Revenue" });
    trackEvent("spb_pending_revenue_report_viewed");
  }
};
</script>

<style lang="scss" scoped>
:deep(td) {
  vertical-align: top !important;
}
</style>
